import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { noop } from '@adsk/alloy-react-helpers';
import { LocaleProvider, supportedLocales, useLocale } from '@adsk/alloy-react-locale';
import { NotificationsProvider } from '@adsk/alloy-react-notification';
import { Reset } from '@adsk/alloy-react-theme';
import { AccountEntitlementsProvider } from './AccountEntitlementsContext';
import { AlloyLaunchDarklyProvider } from './AlloyLaunchDarklyProvider';
import AlloyLaunchDarklySubscriber from './AlloyLaunchDarklySubscriber';
import { CompaniesProvider } from './CompaniesContext';
import { AUTO_FETCH_TYPES, PRODUCT_IDS, TOOL_IDS } from './consts';
import { CurrentAccountIdProvider } from './CurrentAccountIdContext';
import { CurrentProductIdProvider } from './CurrentProductIdContext';
import { CurrentProjectIdProvider } from './CurrentProjectIdContext';
import { CurrentToolIdProvider } from './CurrentToolIdContext';
import { DeletedUsersProvider } from './DeletedUsers';
import { ProjectEntitlementsProvider, useProjectEntitlements, useUser } from './ProjectEntitlementsContext';
import { RolesProvider } from './RolesContext';
import { SubscriptionsProvider } from './SubscriptionsContext';
import { UserProvider } from './UserContext';
import { UsersProvider } from './UsersContext';
const getRedirectUrl = error => {
  if (error && error.hasOwnProperty('redirectUrl')) {
    return error.redirectUrl;
  }
  return undefined;
};
const CheckRedirectEntitlementsResponse = () => {
  const {
    error
  } = useProjectEntitlements();
  const redirectUrl = getRedirectUrl(error);
  useEffect(() => {
    if (redirectUrl) {
      console.error('AlloyProvider - redirecting to redirectUrl');
      window.location.assign(redirectUrl);
    }
  }, [redirectUrl]);
  return null;
};
const LocaleSetter = () => {
  const {
    user
  } = useUser();
  const localeFromEntitlements = user === null || user === void 0 ? void 0 : user.locale;
  const oxygenLocal = supportedLocales.find(locale => locale === localeFromEntitlements);
  const {
    setLocale
  } = useLocale(noop);
  useEffect(() => {
    if (oxygenLocal && setLocale) {
      setLocale(oxygenLocal);
    }
  }, [oxygenLocal, setLocale]);
  return null;
};
/**
 * AlloyProvider to mount all project data context providers.
 */
const AlloyProvider = _ref => {
  let {
    children,
    projectId,
    accountId,
    productId,
    toolId,
    autoFetchEntities = Object.values(AUTO_FETCH_TYPES),
    locale
  } = _ref;
  return /*#__PURE__*/React.createElement(AlloyLaunchDarklyProvider, null, /*#__PURE__*/React.createElement(LocaleProvider, {
    locale: locale
  }, /*#__PURE__*/React.createElement(Reset, null), /*#__PURE__*/React.createElement(NotificationsProvider, null, /*#__PURE__*/React.createElement(CurrentProductIdProvider, {
    productId: productId
  }, /*#__PURE__*/React.createElement(CurrentToolIdProvider, {
    toolId: toolId
  }, /*#__PURE__*/React.createElement(CurrentProjectIdProvider, {
    projectId: projectId
  }, /*#__PURE__*/React.createElement(ProjectEntitlementsProvider, null, /*#__PURE__*/React.createElement(CheckRedirectEntitlementsResponse, null), /*#__PURE__*/React.createElement(CurrentAccountIdProvider, {
    accountId: accountId
  }, /*#__PURE__*/React.createElement(AccountEntitlementsProvider, {
    disabled: !autoFetchEntities.includes(AUTO_FETCH_TYPES.CURRENT_ACCOUNTS_ENTITLEMENTS)
  }, /*#__PURE__*/React.createElement(UserProvider, {
    disabled: true
  }, /*#__PURE__*/React.createElement(AlloyLaunchDarklySubscriber, null), /*#__PURE__*/React.createElement(LocaleSetter, null), /*#__PURE__*/React.createElement(DeletedUsersProvider, null, /*#__PURE__*/React.createElement(UsersProvider, null, /*#__PURE__*/React.createElement(CompaniesProvider, null, /*#__PURE__*/React.createElement(RolesProvider, null, /*#__PURE__*/React.createElement(SubscriptionsProvider, null, children)))))))))))))));
};
AlloyProvider.displayName = 'AlloyProvider';
AlloyProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
  /** Project ID to fetch data for (entitlements/users/roles/companies etc.) */
  projectId: PropTypes.string,
  /** The current account ID */
  accountId: PropTypes.string,
  /** The current project ID (build/docs etc.) */
  productId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** The current project ID (build/docs etc.) */
  toolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Enable auto fetch for specific types */
  autoFetchEntities: PropTypes.arrayOf(PropTypes.oneOf(Object.values(AUTO_FETCH_TYPES))),
  /** Locale, defaults to 'en' */
  locale: PropTypes.string
};
export default Object.assign(AlloyProvider, {
  AUTO_FETCH_TYPES
});
//# sourceMappingURL=AlloyProvider.js.map